import { useCallback, useRef, useState } from 'react';
import { TFunctionRow } from '../helpers/functionRows.helper';

/** Extracts unique user IDs from function rows. */
const extractUserIds = (rows: TFunctionRow[]): string[] => [...new Set(rows.flatMap((r) => r.userIds))];

/**
 * Manages function row state and syncs the derived user IDs to a react-hook-form field.
 * Used by CreateApprovalDialog and UpdateApprovalDialog when the approval functions feature is ON.
 *
 * Returns [rows, handleChange] - calling handleChange updates state AND syncs form value,
 * so it can be used both as the FunctionUserSelect onChange and to reset rows on dialog open.
 */
const useFunctionRows = (
  initialRows: TFunctionRow[],
  onSync: (ids: string[]) => void
): [TFunctionRow[], (rows: TFunctionRow[]) => void] => {
  const [rows, setRows] = useState<TFunctionRow[]>(initialRows);
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;

  const handleChange = useCallback((newRows: TFunctionRow[]) => {
    setRows(newRows);
    onSyncRef.current(extractUserIds(newRows));
  }, []);

  return [rows, handleChange];
};

export default useFunctionRows;
